import { clientStorage } from "@/lib/clientStorage";
import { useState, useCallback, useEffect } from "react";
import LacwWordmark from "@/components/LacwWordmark";
import ShareDraftButtons from "@/components/ShareDraftButtons";
import CloudDraftsPanel from "@/components/CloudDraftsPanel";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { 
  ArrowLeft, Printer, RotateCcw, User, CheckCircle2, Download, ShieldCheck, CheckSquare,
  BriefcaseBusiness, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { base44 } from "@/api/base44Client";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import ActionstepSavePanel from "@/components/ActionstepSavePanel";
import { INTAKE_EMAIL } from "@/lib/demoConfig";

const DEMO_ATA_DATA = {
  client_name: "Christopher Martin",
  client_dob: "1978-06-29",
  client_address: "7A Heritage Drive, Mill Park VIC 3082",
  
  // Authorisations (Checkboxes)
  vals_act: true,
  request_info: true,
  obtain_reports: true,
  disclose_third_parties: true,
  advice_sms_email: true,
  discuss_csos: true,
  contact_centrelink: true,
  gather_stats: true,
  prepare_case_study: true,
  risk_mgmt_review: true,

  // Representative Declaration
  opportunity_questions: true,
  read_and_ensure_understood: true,
  client_accepted: true,
  verbal_consent: true,

  // Signature Block
  staff_title: "Paralegal",
  staff_name: "Greta Kantor",
  staff_signature: "Greta Kantor",
  sign_date: "2026-03-30",
};

const EMPTY_FORM = {
  client_name: "",
  client_dob: "",
  client_address: "",
  
  vals_act: false,
  request_info: false,
  obtain_reports: false,
  disclose_third_parties: false,
  advice_sms_email: false,
  discuss_csos: false,
  contact_centrelink: false,
  gather_stats: false,
  prepare_case_study: false,
  risk_mgmt_review: false,

  opportunity_questions: false,
  read_and_ensure_understood: false,
  client_accepted: false,
  verbal_consent: false,

  staff_title: "",
  staff_name: "",
  staff_signature: "",
  sign_date: "",
};

// Removed: MOCK_ACTIONSTEP_MATTERS replaced by live ActionstepSavePanel

export default function VALSAuthorityToAct({ isPortalMode, brandType = "vals" }) {
  const brandKey = brandType ? `${brandType}_` : "";
  const demographicsKey = `${brandKey}active_client_demographics`;
  const ataPrefillKey = `${brandKey}vals_ata_prefill_pending`;
  const ataListKey = `${brandKey}vals_completed_atas_list`;
  const singleAtaKey = `${brandKey}vals_completed_ata`;

  const orgName = brandType === "lacw" ? "Law and Advocacy Centre for Women" : brandType === "jbl" ? "Jarrod Behan Law" : "Victorian Aboriginal Legal Service";
  const orgShort = brandType === "lacw" ? "LACW" : brandType === "jbl" ? "JBL" : "VALS";
  const orgAddress = brandType === "lacw" ? "RMIT Building 152, Level 1 · 147–155 Pelham Street, Carlton VIC 3053" : brandType === "jbl" ? "Melbourne VIC 3000" : "273 High St, Preston VIC 3072";
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const logAtaToOutbox = async (actionType = "Saved") => {
    try {
      const clientName = formData.client_name || "Unnamed Client";
      const brandName = orgShort || "VALS";
      const emailSubject = `${brandName} Authority to Act - ${clientName} (${actionType})`;

      const clauses = [
        { field: "vals_act", text: "Authorise to act" },
        { field: "request_info", text: "Request & receive info" },
        { field: "obtain_reports", text: "Obtain medical reports" },
        { field: "disclose_third_parties", text: "Disclose to third parties" },
        { field: "advice_sms_email", text: "Advice via SMS/Email" },
        { field: "discuss_csos", text: "Discuss with non-legal caseworkers/CSOs" },
        { field: "contact_centrelink", text: "Contact Centrelink" },
        { field: "gather_stats", text: "Gather statistical database info" },
        { field: "prepare_case_study", text: "Prepare de-identified case study" },
        { field: "risk_mgmt_review", text: "Risk management file reviews" }
      ];

      const consentedClauses = clauses
        .filter(c => formData[c.field] === true)
        .map(c => `- ${c.text}`)
        .join("\n");

      const emailBody = `
Authority to Act & Disclose Information
--------------------------------------
Client Name: ${clientName}
DOB: ${formData.client_dob || "N/A"}
Address: ${formData.client_address || "N/A"}

Representative Details:
  Title: ${formData.staff_title || "N/A"}
  Name: ${formData.staff_name || "N/A"}
  Digital Signature: ${formData.staff_signature || "N/A"}
  Sign Date: ${formData.sign_date || "N/A"}

Representative Declarations:
  Opportunity for questions: ${formData.opportunity_questions ? "Yes" : "No"}
  Read and understood: ${formData.read_and_ensure_understood ? "Yes" : "No"}
  Client accepted: ${formData.client_accepted ? "Yes" : "No"}
  Verbal consent received: ${formData.verbal_consent ? "Yes" : "No"}

Consented Clauses:
${consentedClauses || "- No clauses selected"}
      `.trim();

      await base44.integrations.Core.SendEmail({
        to: INTAKE_EMAIL,
        cc: "",
        subject: emailSubject,
        body: emailBody
      });
    } catch (err) {
      console.warn("Failed to write Authority to Act to outbox:", err);
    }
  };

  // Actionstep integration handled by ActionstepSavePanel + useActionstepSession hook

  // Auto-prefill if redirected from Intake Form prefill action
  useEffect(() => {
    const pending = clientStorage.getItem(ataPrefillKey);
    if (pending === 'true') {
      clientStorage.removeItem(ataPrefillKey);
      // Delay slightly to ensure state is settled
      setTimeout(() => {
        prefillFromIntake();
      }, 150);
    }
  }, [ataPrefillKey]);



  const saveAtaToLocalStorage = (currentData) => {
    try {
    const ataRecord = {
      id: `${orgShort.toLowerCase()}-ata-completed-${Date.now()}`,
      name: `Completed ${orgShort} Authority to Act - ${currentData.client_name || "Christopher Martin"}.pdf`,
      clientName: currentData.client_name,
      date: currentData.sign_date || new Date().toISOString().split('T')[0],
        timestamp: Date.now(),
        formData: currentData,
        type: 'ATA'
      };
      
      const existingStr = clientStorage.getItem(ataListKey);
      let list = [];
      if (existingStr) {
        try { list = JSON.parse(existingStr); } catch (e) {}
      }
      
      // Filter out duplicates
      list = list.filter(item => item.clientName !== currentData.client_name);
      list.push(ataRecord);
      
      clientStorage.setItem(singleAtaKey, JSON.stringify(ataRecord));
      clientStorage.setItem(ataListKey, JSON.stringify(list));
      
      // Fire custom event to notify other open forms in session
      window.dispatchEvent(new Event("storage"));
    } catch (e) {
      console.error("Failed to save ATA to clientStorage:", e);
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const prefillFromIntake = () => {
    try {
      const activeClientStr = clientStorage.getItem(demographicsKey);
      if (!activeClientStr) {
        toast.error(`No active client found. Please fill out details in the ${orgShort} Intake Form first.`);
        return;
      }
      const client = JSON.parse(activeClientStr);
      
      const clientName = `${client.first_name || ""} ${client.surname || ""}`.trim();
      const clientDob = client.dob || "";
      
      // Compile address
      const addressParts = [];
      if (client.res_address) addressParts.push(client.res_address);
      if (client.res_suburb) addressParts.push(client.res_suburb);
      if (client.res_postcode) addressParts.push(`VIC ${client.res_postcode}`);
      const clientAddress = addressParts.join(", ");
      
      if (!clientName && !clientDob && !clientAddress) {
        toast.error("Active client details in clientStorage are empty.");
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        client_name: clientName || prev.client_name,
        client_dob: clientDob || prev.client_dob,
        client_address: clientAddress || prev.client_address
      }));
      
      toast.success(`Prefilled details for ${clientName || "Client"} from Intake Form.`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to prefill from Intake Form.");
    }
  };

  const loadDemoCase = () => {
    setFormData(DEMO_ATA_DATA);
    toast.success("Loaded demo case: Christopher Martin");
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    toast.info("Form cleared");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name || !formData.staff_name) {
      toast.error("Client Name and Staff Name are required.");
      return;
    }
    
    setIsSubmitting(true);
    await logAtaToOutbox("Saved");
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    
    saveAtaToLocalStorage(formData);
    toast.success(`${orgShort} Authority to Act successfully logged & saved.`);
  };

  const generatePDF = async () => {
    const formElement = document.getElementById("ata-form-content");
    const canvas = await html2canvas(formElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff"
    });
    
    const imgData = canvas.toDataURL("image/png");
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });
    
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    
    doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= 297;
    
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      doc.addPage();
      doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= 297;
    }
    
    return doc;
  };

  const handlePrint = async () => {
    try {
      const doc = await generatePDF();
      saveAtaToLocalStorage(formData);
      await logAtaToOutbox("Printed");
      doc.autoPrint();
      window.open(doc.output("bloburi"));
      setShowExportDialog(false);
    } catch (e) {
      toast.error("Failed to compile print view.");
    }
  };

  const handleDownload = async () => {
    try {
      const doc = await generatePDF();
      saveAtaToLocalStorage(formData);
      await logAtaToOutbox("Downloaded");
      doc.save(`${orgShort}_Authority_To_Act_${formData.client_name.replace(/\s+/g, "_")}.pdf`);
      setShowExportDialog(false);
      toast.success("PDF saved to computer.");
    } catch (e) {
      toast.error("Failed to generate PDF file.");
    }
  };

  // Build the ATA file note text for Actionstep
  const buildAtaNoteText = () => {
    const clauses = [
      { field: "vals_act", text: "Authorise to act" },
      { field: "request_info", text: "Request & receive info" },
      { field: "obtain_reports", text: "Obtain medical reports" },
      { field: "disclose_third_parties", text: "Disclose to third parties" },
      { field: "advice_sms_email", text: "Advice via SMS/Email" },
      { field: "discuss_csos", text: "Discuss with non-legal caseworkers/CSOs" },
      { field: "contact_centrelink", text: "Contact Centrelink" },
      { field: "gather_stats", text: "Gather statistical database info" },
      { field: "prepare_case_study", text: "Prepare de-identified case study" },
      { field: "risk_mgmt_review", text: "Risk management file reviews" },
    ];
    const consented = clauses.filter(c => formData[c.field]).map(c => `- ${c.text}`).join("\n");
    return [
      `CLIENT: ${formData.client_name || "N/A"}`,
      `DOB: ${formData.client_dob || "N/A"}`,
      `ADDRESS: ${formData.client_address || "N/A"}`,
      ``,
      `REPRESENTATIVE: ${formData.staff_name || "N/A"} (${formData.staff_title || "N/A"})`,
      `SIGN DATE: ${formData.sign_date || "N/A"}`,
      ``,
      `DECLARATIONS:`,
      `  Opportunity for questions: ${formData.opportunity_questions ? "Yes" : "No"}`,
      `  Read and understood: ${formData.read_and_ensure_understood ? "Yes" : "No"}`,
      `  Client accepted: ${formData.client_accepted ? "Yes" : "No"}`,
      `  Verbal consent: ${formData.verbal_consent ? "Yes" : "No"}`,
      ``,
      `CONSENTED CLAUSES:`,
      consented || "- None selected",
    ].join("\n");
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      
      {/* Top sticky navigation bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!isPortalMode && (
              <>
                <a
                  href={createPageUrl("Home")}
                  className="text-slate-500 hover:text-red-600 transition-colors flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </a>
                <div className="h-6 w-px bg-slate-200 mx-2" />
              </>
            )}
            <div>
              <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                {!isPortalMode && (
                  brandType === "lacw" ? (
                    <LacwWordmark compact />
                  ) : brandType === "jbl" ? (
                    <img
                      src="/jbl-logo.png"
                      alt="JBL Logo"
                      className="h-7 w-auto object-contain"
                    />
                  ) : (
                    <img
                      src="/vals-logo.png"
                      alt="VALS Logo"
                      className="h-7 w-auto object-contain"
                    />
                  )
                )}
                {orgShort} Authority to Act Form
              </h1>
              <p className="text-xs text-slate-500">{orgName} · Digital Authorization</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={prefillFromIntake} className="text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Prefill from Intake Form
            </Button>
            <Button variant="outline" size="sm" onClick={loadDemoCase} className="text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50">
              Load Demo Case (Christopher)
            </Button>
            <Button variant="outline" size="sm" onClick={resetForm} className="text-xs font-semibold text-slate-500">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button size="sm" onClick={() => setShowExportDialog(true)} className="bg-red-600 hover:bg-red-700 text-white font-semibold text-xs gap-1.5">
              <Printer className="w-3.5 h-3.5" /> Export / Print
            </Button>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        
        {/* Form Body for Export */}
        <div id="ata-form-content" className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm space-y-6">
          
          {/* Header */}
          <div className="border-b-2 border-red-600 pb-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900 uppercase">{orgName}</h2>
              <h3 className="text-sm font-bold text-red-600 uppercase tracking-wide mt-1">Authority to Act, Obtain and Disclose Information</h3>
              <p className="text-xs text-slate-500 mt-1">Digital client consent &amp; casework authority record</p>
            </div>
            {brandType === "lacw" ? (
              <LacwWordmark compact />
            ) : brandType === "jbl" ? (
              <img
                src="/jbl-logo.png"
                alt="JBL Logo"
                className="h-10 object-contain"
              />
            ) : (
              <img
                src="/vals-logo.png"
                alt="VALS Logo"
                className="h-10 object-contain"
              />
            )}
          </div>

          <form onSubmit={handleFormSubmit} className="space-y-6">
            
            {/* Section 1: Client details */}
            <div className="bg-slate-50 p-4 border border-slate-100 rounded-xl space-y-4">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <User className="w-4 h-4 text-red-600" /> Client Demographics
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Client Full Name *</Label>
                  <Input 
                    value={formData.client_name} 
                    onChange={e => handleFieldChange("client_name", e.target.value)} 
                    placeholder="e.g. Christopher Martin"
                    required
                    className="text-sm bg-white rounded-lg"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Date of Birth</Label>
                  <Input 
                    type="date" 
                    value={formData.client_dob} 
                    onChange={e => handleFieldChange("client_dob", e.target.value)} 
                    className="text-sm bg-white rounded-lg font-mono"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Residential Address</Label>
                <Input 
                  value={formData.client_address} 
                  onChange={e => handleFieldChange("client_address", e.target.value)} 
                  placeholder="Street Address, Suburb, Postcode"
                  className="text-sm bg-white rounded-lg"
                />
              </div>
            </div>



            {/* Section 2: Authorizations List */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
                <CheckSquare className="w-4 h-4 text-red-600" /> Client Authorisation Clauses
              </h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                By signing this document, the client authorises {orgName} ({orgShort}) to act on their behalf and execute the following checks, disclosures, and enquiries:
              </p>

              <div className="space-y-3.5">
                {[
                  { field: "vals_act", text: `Authorise ${orgName} (${orgShort}) to act on my behalf.` },
                  { field: "request_info", text: "Request and receive personal information and documentation in relation to me." },
                  { field: "obtain_reports", text: "Obtain a letter/s and/or report/s from my treating medical practitioner/s, other health professionals, and other support workers for the purposes of acting on my behalf." },
                  { field: "disclose_third_parties", text: "Disclose relevant personal information, documentation, and reports about me to third parties for the purposes of acting on my behalf." },
                  { field: "advice_sms_email", text: "Provide me with legal advice or communicate with me, including by the use of email and/or text (SMS) message." },
                  { field: "discuss_csos", text: `Discuss my legal matter or any other matter relating to me with ${brandType === "lacw" ? "LACW's staff" : brandType === "jbl" ? "JBL's staff" : "VALS' Client Service Officers"} or other non-legal caseworkers I am involved with.` },
                  { field: "contact_centrelink", text: "Contact Centrelink and obtain personal information about me." },
                ].map(item => (
                  <label key={item.field} className="flex items-start gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors border border-transparent hover:border-slate-100">
                    <input 
                      type="checkbox" 
                      checked={formData[item.field]} 
                      onChange={e => handleCheckboxChange(item.field, e.target.checked)}
                      className="w-4.5 h-4.5 rounded border-slate-300 text-red-600 focus:ring-red-500 mt-0.5 accent-red-600"
                    />
                    <span className="text-xs font-semibold text-slate-700 leading-relaxed">{item.text}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Section 3: Data & Statistics (Advocacy purposes) */}
            <div className="space-y-4 border-t border-slate-100 pt-4">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Additional Purposes &amp; Statistics Consent</h4>
              <div className="space-y-3.5">
                {[
                  { field: "gather_stats", text: "Gathering statistical database information." },
                  { field: "prepare_case_study", text: `Preparing a de-identified summary of my legal matter as a case study for ${orgShort}'s use in its policy and advocacy work, and for use in publications.` },
                  { field: "risk_mgmt_review", text: "Risk management purposes including file reviews by other community legal services." },
                ].map(item => (
                  <label key={item.field} className="flex items-start gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors border border-transparent hover:border-slate-100">
                    <input 
                      type="checkbox" 
                      checked={formData[item.field]} 
                      onChange={e => handleCheckboxChange(item.field, e.target.checked)}
                      className="w-4.5 h-4.5 rounded border-slate-300 text-red-600 focus:ring-red-500 mt-0.5 accent-red-600"
                    />
                    <span className="text-xs font-semibold text-slate-700 leading-relaxed">{item.text}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Section 4: Representative Declaration */}
            <div className="space-y-4 border-t border-red-100 pt-4 bg-red-50/20 p-4 rounded-xl border border-red-100/50">
              <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-red-700" /> Representative Declaration
              </h4>
              <p className="text-[11px] text-red-700 font-medium">
                The caseworker must confirm the verbal or physical signature details before lodging:
              </p>
              
              <div className="space-y-2.5">
                {[
                  { field: "opportunity_questions", text: "I have given the client the opportunity to ask questions about the authority to act." },
                  { field: "read_and_ensure_understood", text: "I have taken steps to read and ensure that the client understood the authority to act." },
                  { field: "client_accepted", text: "I am satisfied that the client’s response indicated acceptance of the authority to act." },
                  { field: "verbal_consent", text: "I have received verbal consent from the client in respect of this authority in lieu of a physical signature." },
                ].map(item => (
                  <label key={item.field} className="flex items-start gap-3 cursor-pointer p-1">
                    <input 
                      type="checkbox" 
                      checked={formData[item.field]} 
                      onChange={e => handleCheckboxChange(item.field, e.target.checked)}
                      className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500 mt-0.5 accent-red-600"
                    />
                    <span className="text-xs font-bold text-red-800 leading-normal">{item.text}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Section 5: Signature block */}
            <div className="border-t border-slate-200 pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Representative Title</Label>
                <Input 
                  value={formData.staff_title} 
                  onChange={e => handleFieldChange("staff_title", e.target.value)} 
                  placeholder="e.g. Paralegal / Solicitor"
                  className="text-sm bg-white rounded-lg font-semibold"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Representative Full Name</Label>
                <Input 
                  value={formData.staff_name} 
                  onChange={e => handleFieldChange("staff_name", e.target.value)} 
                  placeholder="Staff Name"
                  className="text-sm bg-white rounded-lg font-semibold"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Digital Signature</Label>
                <Input 
                  value={formData.staff_signature} 
                  onChange={e => handleFieldChange("staff_signature", e.target.value)} 
                  placeholder="Type signature"
                  className="text-sm bg-white rounded-lg font-mono italic"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Sign Date</Label>
                <Input 
                  type="date" 
                  value={formData.sign_date} 
                  onChange={e => handleFieldChange("sign_date", e.target.value)} 
                  className="text-sm bg-white rounded-lg font-semibold"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 print:hidden items-center">
              <ShareDraftButtons formData={formData} onImport={setFormData} brand={orgShort.toLowerCase()} formName="authority_to_act" />
              <CloudDraftsPanel formType="authority_to_act" formData={formData} onLoadDraft={setFormData} brand={orgShort.toLowerCase()} />
              <Button 
                type="button" 
                variant="outline" 
                onClick={resetForm}
                className="gap-2 rounded-xl text-slate-600"
              >
                <RotateCcw className="w-4 h-4" /> Reset Form
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-red-600 hover:bg-red-700 text-white gap-2 px-8 rounded-xl font-bold"
              >
                {isSubmitting ? "Latching..." : (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Save Authority Form
                  </>
                )}
              </Button>
            </div>

          </form>

          {/* Actionstep Save Section */}
          <div className="mt-6 border-t border-slate-200 pt-6 print:hidden">
            <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1 text-left">
                <h4 className="text-sm font-bold text-red-900 flex items-center gap-1.5">
                  <BriefcaseBusiness className="w-4 h-4 text-red-700" />
                  Save to Actionstep
                </h4>
                <p className="text-xs text-red-700 leading-normal">
                  Save a file note for this Authority to Act directly to the linked Actionstep matter.
                </p>
              </div>
              <ActionstepSavePanel
                noteText={buildAtaNoteText()}
                formLabel="Authority to Act"
                accentColor="#8C1D18"
                disabled={!formData.client_name}
              />
            </div>
          </div>

          {/* Footer info block */}
          <div className="mt-8 text-center text-slate-400 text-xs space-y-1 border-t border-slate-100 pt-6">
            <p>{orgName} · {orgAddress}</p>
            <p>Confidential Client Document · For Legal Representation Use Only</p>
          </div>

        </div>

      </div>

      {/* Dialog for Print / PDF Export */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900">Export {orgShort} Authority to Act</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Select your preferred export method to save or print the client file details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2.5 py-4">
            <Button 
              onClick={handleDownload} 
              className="w-full flex items-center justify-start gap-3 bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-sm font-semibold rounded-xl p-4 h-12"
            >
              <Download className="w-4 h-4 text-red-600" />
              <span>Save to Computer (PDF)</span>
            </Button>
            <Button 
              onClick={handlePrint} 
              className="w-full flex items-center justify-start gap-3 bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-sm font-semibold rounded-xl p-4 h-12"
            >
              <Printer className="w-4 h-4 text-red-600" />
              <span>Print Document / Save to PDF</span>
            </Button>
          </div>
          <div className="flex justify-end pt-1">
            <Button variant="ghost" onClick={() => setShowExportDialog(false)} className="text-xs rounded-lg text-slate-500">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Actionstep dialog is now handled internally by ActionstepSavePanel */}

    </div>
  );
}
